import { Information } from "@carbon/icons-react";
import { Tooltip } from "@carbon/react";
import { FC, ReactNode } from "react";

import EmptyTableState from "src/components/DataStates/EmptyTableState.tsx";
import {
  PropertyValueRenderer,
  PropertyValueRendererParams,
} from "src/dynamic-view-renderer/Widgets/PropertyRenderer.tsx";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useT } from "src/internationalization";

export const BasicWidgetHorizontalPropertyLayout: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex gap-7 flex-wrap justify-between items-start">{children}</div>
);

export const BasicWidgetHorizontalProperty: FC<
  WidgetProperty & Pick<BasicWidgetHorizontalProps, "propertyValueRendererParams">
> = ({
  label,
  propertyValueRendererParams,
  comparisonValue,
  comparisonIcon,
  mismatch,
  ...rest
}) => {
  const t = useT();

  return (
    <div className="flex flex-col gap-3">
      <p
        className="text-sm text-textSecondary"
        style={{ flex: comparisonValue ? "0 1 auto" : "1 1 auto" }}
      >
        {label}
      </p>
      {comparisonValue && (
        <div className="text-sm gap-3 flex items-center" style={{ flex: "0 1 auto" }}>
          {comparisonIcon && <img src={comparisonIcon} alt={comparisonIcon} />}
          {comparisonValue}
          {mismatch && (
            <Tooltip
              align="bottom-right"
              as={"p"}
              label={t("component.widget.basic.property.mismatch.tooltip")}
              className="text-sm block h-5"
            >
              <Information className="text-supportError" />
            </Tooltip>
          )}
        </div>
      )}
      <p className="text-sm" style={{ flex: "0 1 auto" }}>
        <PropertyValueRenderer label={label} {...rest} params={propertyValueRendererParams} />
      </p>
    </div>
  );
};

type BasicWidgetHorizontalProps = {
  properties: WidgetProperty[];
  propertyValueRendererParams?: PropertyValueRendererParams;
} & Omit<WidgetContainerProps, "children">;

export const BasicWidgetHorizontal: FC<Omit<BasicWidgetHorizontalProps, "orientation">> = ({
  properties,
  propertyValueRendererParams,
  ...containerProps
}) => (
  <WidgetContainer {...containerProps}>
    <BasicWidgetHorizontalPropertyLayout>
      {properties.map((property, index) => (
        <BasicWidgetHorizontalProperty
          {...property}
          key={index}
          propertyValueRendererParams={propertyValueRendererParams}
        />
      ))}
      {properties.length === 0 && <EmptyTableState />}
    </BasicWidgetHorizontalPropertyLayout>
  </WidgetContainer>
);
